import {
  IApiPage,
  IListing,
  IMarketFilters,
  ISelectOption,
} from "../common/commonTypes";
import { listingService } from "../api/listingService";
import { useInfiniteQuery, useQuery } from "react-query";
import { QueryKeys } from "../common/queryKeys";
import { nextPageParamGetter } from "../helpers/paginationHelper";
import { getMarketQueryFilters } from "../helpers/marketHelper";
import { distinctService } from "../api/distinctService";
import { languagesOptions } from "../common/languages";
import { genresOptions } from "../common/genres";
import { ListingStatusEnum } from "../common/commonEnums";

const MARKET_PAGE_SIZE = 12;

export const fetchMarketPage = async (
  pageParam: number,
  filters: IMarketFilters
): Promise<IApiPage<IListing>> => {
  const response = await listingService.fetchListings({
    ...getMarketQueryFilters(filters),
    offset: pageParam,
    limit: MARKET_PAGE_SIZE,
  });

  return response.data as IApiPage<IListing>;
};

export const useMarketQuery = (filters: IMarketFilters) => {
  return useInfiniteQuery(
    [QueryKeys.marketData, filters],
    async ({ pageParam = 0 }) => fetchMarketPage(pageParam, filters),
    {
      getNextPageParam: nextPageParamGetter(MARKET_PAGE_SIZE),
    }
  );
};

export const fetchMarketFilters = async (): Promise<{
  languages: ISelectOption[];
  genres: ISelectOption[];
}> => {
  const result = await distinctService.fetchDistinct({
    table: "Listing",
    attributes: ["languages", "genres"],
    where: {
      status: {
        $notIn: [
          ListingStatusEnum.cancelled,
          ListingStatusEnum.ended,
          ListingStatusEnum.failed,
        ],
      },
    },
  });
  const languages = languagesOptions.filter((lang) =>
    Object.keys(result.data.languages).find((key) => key === lang.value)
  );
  const genres = genresOptions.filter((genre) =>
    Object.keys(result.data.genres).find((key) => key === genre.value)
  );

  return {
    languages,
    genres,
  };
};

export const useMarketFilters = () => {
  const queryResponse = useQuery(QueryKeys.marketFilters, fetchMarketFilters);

  return {
    filters: {
      languages: queryResponse.data?.languages || languagesOptions,
      genres: queryResponse.data?.genres || genresOptions,
    },
    ...queryResponse,
  };
};
